/**
 * InvoicePDF — Premium Fintech-Grade PDF for @react-pdf/renderer
 *
 * Design principles:
 *   1. Compact density: ~15 line items fit on a single A4 page
 *   2. wrap={false} on totals + bank details so they never split across pages
 *   3. Fixed mini-header on page 2+ with invoice # and page number
 *   4. Strict flex: 1 columns (yoga engine's only reliable equal-width pattern)
 *   5. Clear visual hierarchy: labels → headings → body → meta
 */
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { PdfPageFooter } from '../../../lib/PdfPageFooter';
import { isWhiteLabelTier, canUseBankDetails } from '../../../lib/tiers';
import { DEFAULT_DOCUMENT_ACCENT } from '../../../lib/brandAccent';

import { DOC } from '../../../lib/documentPalette';
import { extraWorkInvoiceNote } from '../../../lib/extraWork';

const C = DOC;

// ─── Stylesheet ─────────────────────────────────────────────
const s = StyleSheet.create({
    /* Page */
    page: {
        fontFamily: 'Helvetica',
        fontSize: 9,
        lineHeight: 1.4,
        color: C.body,
        backgroundColor: C.white,
        paddingBottom: 40,      // reserve space for fixed footer
    },

    /* Fixed mini-header for page 2+ */
    continuationHeader: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8 35',
        backgroundColor: C.bg,
        borderBottom: `1px solid ${C.line}`,
    },
    continuationText: { fontSize: 7, fontFamily: 'Helvetica-Bold', color: C.muted },

    /* ─── Header ─── */
    header: { padding: '24 35 18 35', borderBottom: `1px solid ${C.line}` },

    logoRow: { marginBottom: 16 },
    logoBox: { width: 48, height: 48 },
    logo: { width: '100%', height: '100%', objectFit: 'contain', borderRadius: 4 },

    /* 3-column grid */
    row: { flexDirection: 'row', alignItems: 'flex-start' },
    col1Left: { flex: 0.7, flexShrink: 0, flexGrow: 0.7, paddingRight: 45 },
    col2Mid: { flex: 1.3, flexShrink: 0, flexGrow: 1.3, paddingRight: 16 },
    colLast: { flex: 1, flexShrink: 0, flexGrow: 1, alignItems: 'flex-end' },

    label: { fontSize: 7, fontFamily: 'Helvetica-Bold', color: C.label, textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 6 },
    h1: { fontSize: 12, fontFamily: 'Helvetica-Bold', color: C.black, marginBottom: 2 },
    h2: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: C.black, marginBottom: 2 },
    sm: { fontSize: 8, color: C.muted, marginBottom: 1.5 },
    vat: { fontSize: 7, color: C.label, marginTop: 4 },

    vehicleLine: { fontSize: 8, color: C.muted, marginTop: 8, paddingTop: 5, borderTop: `1px solid ${C.faintLine}` },
    vrm: { fontFamily: 'Helvetica-Bold', color: C.black },

    invTitle: { fontSize: 14, fontFamily: 'Helvetica-Bold', color: C.black, letterSpacing: 1.5, textTransform: 'uppercase', textAlign: 'right', marginBottom: 2 },
    invNumber: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: C.muted, textAlign: 'right', marginBottom: 0 },

    metaBox: { alignItems: 'flex-end', marginTop: 10 },
    metaRow: { flexDirection: 'row', justifyContent: 'flex-end', marginBottom: 4 },
    metaLabel: { fontSize: 7, color: C.label, marginRight: 6 },
    metaVal: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: C.black, textAlign: 'right' },

    /* ─── Table ─── */
    table: { padding: '14 35 6 35' },
    thead: { flexDirection: 'row', borderBottom: `2px solid ${C.line}`, paddingBottom: 6, marginBottom: 2 },
    th: { fontSize: 7, fontFamily: 'Helvetica-Bold', color: C.label, textTransform: 'uppercase', letterSpacing: 0.6 },
    tr: { flexDirection: 'row', paddingVertical: 7, borderBottom: `1px solid ${C.faintLine}` },
    cDesc: { flex: 3 },
    cQty: { flex: 0.5, textAlign: 'right' },
    cPrice: { flex: 1, textAlign: 'right' },
    cTotal: { flex: 1, textAlign: 'right' },

    itemName: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: C.black },
    itemNote: { fontSize: 7, fontFamily: 'Helvetica', color: C.body, marginTop: 2 },
    badge: { padding: '1.5 4', borderRadius: 2, marginLeft: 5 },
    badgeText: { fontSize: 5.5, textTransform: 'uppercase', fontFamily: 'Helvetica-Bold', letterSpacing: 0.4 },

    /* ─── Totals (wrap={false}) ─── */
    totalsBox: { padding: '14 35', backgroundColor: C.bg, borderTop: `1px solid ${C.line}`, flexDirection: 'row', justifyContent: 'flex-end' },
    totalsInner: { width: 180 },
    tRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 5 },
    tLabel: { color: C.muted, fontSize: 8 },
    tVal: { color: C.dark, fontFamily: 'Helvetica', fontSize: 8, textAlign: 'right' },
    grandRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 6, paddingTop: 6, borderTop: `1px solid ${C.line}` },
    grandVal: { fontSize: 11, fontFamily: 'Helvetica-Bold', color: C.black },
    paidRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 4 },
    paidLabel: { color: C.green, fontSize: 8 },
    paidVal: { color: C.green, fontFamily: 'Helvetica', fontSize: 8, textAlign: 'right' },

    balanceBox: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, padding: 12, borderRadius: 5, backgroundColor: C.black },
    balanceLabel: { fontSize: 11, fontFamily: 'Helvetica-Bold', color: C.white },
    balanceVal: { fontSize: 14, fontFamily: 'Helvetica-Bold', color: C.white },

    /* ─── Bank details (wrap={false}) ─── */
    bankSection: { padding: '14 35', borderTop: `1px solid ${C.line}` },
    bankTitle: { fontSize: 7, fontFamily: 'Helvetica-Bold', color: C.label, textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 8 },
    bankGrid: { backgroundColor: C.bg, border: `1px solid ${C.faintLine}`, borderRadius: 4, padding: 10 },
    bankRow: { flexDirection: 'row', marginBottom: 8 },
    bankCol: { flex: 1, paddingRight: 8 },
    bankItemLabel: { fontSize: 6, color: C.label, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 2 },
    bankItemVal: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: C.black },
    bankItemMono: { fontSize: 9, fontFamily: 'Helvetica', letterSpacing: 1.2, color: C.black },

    /* ─── Notes ─── */
    notesSection: { padding: '10 35 6 35' },
    notesTitle: { fontFamily: 'Helvetica-Bold', fontSize: 8, color: C.black, marginBottom: 3 },
    notesText: { fontSize: 8, color: C.muted },

    /* ─── Footer text ─── */
    footerText: { padding: '8 35 10 35', fontSize: 7, color: C.label, textAlign: 'center' },

    /* ─── Thank you ─── */
    thankYou: { padding: '10 35', textAlign: 'center', fontSize: 8, color: C.muted, fontFamily: 'Helvetica' },
});

// ─── Helpers ────────────────────────────────────────────────
const fmt = (v: number) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v);
const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

// ─── Component ──────────────────────────────────────────────
export const InvoicePDF = ({ invoice, logoBase64 }: { invoice: any; logoBase64?: string }) => {
    const b = invoice.branding_snapshot;
    const accent = b.invoice_accent_color || DEFAULT_DOCUMENT_ACCENT;
    const isMinimal = b.invoice_theme === 'minimal';
    const name = invoice.organizations?.name || b.business_name;
    const whiteLabel = isWhiteLabelTier(b.subscription_tier);
    const showBankDetails = !!b.bank_details?.bank_name && canUseBankDetails(b.subscription_tier);
    const hasLogo = !!(logoBase64 || b.logo_url);

    // Due date fallback
    let dueDate = invoice.due_date;
    if (!dueDate) {
        const d = new Date(invoice.issued_at || invoice.created_at);
        d.setDate(d.getDate() + (b.payment_terms_days || 14));
        dueDate = d.toISOString();
    }

    return (
        <Document>
            <Page size="A4" style={s.page}>

                {/* ── Fixed page footer with page numbers (appears on every page) ── */}
                <PdfPageFooter reference={invoice.invoice_number} branded={!whiteLabel} />

                {/* ── Continuation header for page 2+ ── */}
                <View style={s.continuationHeader} fixed render={({ pageNumber }) => pageNumber > 1 ? (
                    <>
                        <Text style={s.continuationText}>{name} — {invoice.invoice_number} (continued)</Text>
                        <Text style={s.continuationText}>Balance Due: {fmt(invoice.balance_due)}</Text>
                    </>
                ) : null} />

                {/* Solid accent bar. A filled View prints far more reliably than a
                    top border, which some print drivers clip in the non-printable
                    top margin (the reported "missing blue line" on print). */}
                <View style={{ height: isMinimal ? 3 : 6, backgroundColor: accent }} />

                {/* ══════════════════════ HEADER ══════════════════════ */}
                <View style={s.header}>

                    {hasLogo && (
                        <View style={s.logoRow}>
                            <View style={s.logoBox}>
                                <Image src={logoBase64 || b.logo_url} style={s.logo} />
                            </View>
                        </View>
                    )}

                    <View style={s.row}>
                        {/* Col 1 — Business Identity */}
                        <View style={s.col1Left}>
                            <Text style={s.label}>Invoice From:</Text>
                            <Text style={s.h1}>{name}</Text>
                            {b.address && <Text style={s.sm}>{b.address.replace(/\\n/g, '\n')}</Text>}
                            {b.phone && <Text style={s.sm}>{b.phone}</Text>}
                            {b.email && <Text style={s.sm}>{b.email}</Text>}
                            {b.vat_number && <Text style={s.vat}>VAT: {b.vat_number}</Text>}
                        </View>

                        {/* Col 2 — Invoice To */}
                        <View style={s.col2Mid}>
                            <Text style={s.label}>Invoice To:</Text>
                            <Text style={s.h2}>{invoice.customers?.full_name || 'Customer'}</Text>
                            {invoice.customers?.email && <Text style={s.sm}>{invoice.customers.email}</Text>}
                            {invoice.customers?.phone && <Text style={s.sm}>{invoice.customers.phone}</Text>}
                            {invoice.vehicles && (
                                <Text style={s.vehicleLine}>
                                    Vehicle: <Text style={s.vrm}>{invoice.vehicles.vrm}</Text>
                                    {' '}({invoice.vehicles.make} {invoice.vehicles.model}{invoice.vehicles.engine ? ` - ${invoice.vehicles.engine}` : ''}{invoice.vehicles.fuel_type ? ` (${invoice.vehicles.fuel_type})` : ''})
                                </Text>
                            )}
                        </View>

                        {/* Col 3 — Invoice Meta */}
                        <View style={s.colLast}>
                            <Text style={s.invTitle}>INVOICE</Text>
                            <Text style={s.invNumber}>{invoice.invoice_number}</Text>
                            <View style={s.metaBox}>
                                <View style={s.metaRow}>
                                    <Text style={s.metaLabel}>Date:</Text>
                                    <Text style={s.metaVal}>{fmtDate(invoice.issued_at || invoice.created_at)}</Text>
                                </View>
                                <View style={s.metaRow}>
                                    <Text style={s.metaLabel}>Due:</Text>
                                    <Text style={s.metaVal}>{fmtDate(dueDate)}</Text>
                                </View>
                            </View>
                        </View>
                    </View>
                </View>

                {/* ══════════════════════ TABLE ══════════════════════ */}
                <View style={s.table}>
                    <View style={s.thead}>
                        <Text style={[s.th, s.cDesc]}>Description</Text>
                        <Text style={[s.th, s.cQty]}>Qty</Text>
                        <Text style={[s.th, s.cPrice]}>Unit Price</Text>
                        <Text style={[s.th, s.cTotal]}>Total</Text>
                    </View>

                    {invoice.invoice_line_items.map((item: any, i: number) => (
                        <View key={i} style={s.tr} wrap={false}>
                            <View style={s.cDesc}>
                                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                    <Text style={s.itemName}>{item.description}</Text>
                                    {item.item_type && (
                                        <View style={[s.badge, { backgroundColor: `${accent}18` }]}>
                                            <Text style={[s.badgeText, { color: accent }]}>{item.item_type}</Text>
                                        </View>
                                    )}
                                </View>
                                {extraWorkInvoiceNote(item.approval) && (
                                    <Text style={s.itemNote}>{extraWorkInvoiceNote(item.approval)}</Text>
                                )}
                            </View>
                            <Text style={[s.cQty, { fontFamily: 'Helvetica', color: C.body }]}>{Number(item.quantity).toFixed(2)}</Text>
                            <Text style={[s.cPrice, { fontFamily: 'Helvetica', color: C.body }]}>{fmt(item.unit_price)}</Text>
                            <Text style={[s.cTotal, { fontFamily: 'Helvetica-Bold', color: C.black }]}>{fmt(item.line_total)}</Text>
                        </View>
                    ))}
                </View>

                {/* ══════════════════════ TOTALS (never split) ══════════════════════ */}
                <View style={s.totalsBox} wrap={false}>
                    <View style={s.totalsInner}>
                        <View style={s.tRow}>
                            <Text style={s.tLabel}>Subtotal</Text>
                            <Text style={s.tVal}>{fmt(invoice.subtotal)}</Text>
                        </View>
                        {b.is_vat_registered && (
                            <View style={s.tRow}>
                                <Text style={s.tLabel}>VAT (20%)</Text>
                                <Text style={s.tVal}>{fmt(invoice.vat_amount)}</Text>
                            </View>
                        )}
                        <View style={s.grandRow}>
                            <Text style={s.grandVal}>Total</Text>
                            <Text style={s.grandVal}>{fmt(invoice.total_gross)}</Text>
                        </View>
                        {invoice.amount_paid > 0 && (
                            <View style={s.paidRow}>
                                <Text style={s.paidLabel}>Amount Paid</Text>
                                <Text style={s.paidVal}>-{fmt(invoice.amount_paid)}</Text>
                            </View>
                        )}
                        {invoice.balance_due > 0 && (
                            <View style={s.balanceBox}>
                                <Text style={s.balanceLabel}>Balance Due</Text>
                                <Text style={s.balanceVal}>{fmt(invoice.balance_due)}</Text>
                            </View>
                        )}
                    </View>
                </View>

                {/* ══════════════════════ BANK DETAILS (never split) ══════════════════════ */}
                {showBankDetails && (
                    <View style={s.bankSection} wrap={false}>
                        <Text style={s.bankTitle}>Payment Details</Text>
                        <View style={s.bankGrid}>
                            <View style={s.bankRow}>
                                <View style={s.bankCol}>
                                    <Text style={s.bankItemLabel}>Bank Name</Text>
                                    <Text style={s.bankItemVal}>{b.bank_details.bank_name}</Text>
                                </View>
                                <View style={s.bankCol}>
                                    <Text style={s.bankItemLabel}>Account Name</Text>
                                    <Text style={s.bankItemVal}>{b.bank_details.account_name}</Text>
                                </View>
                            </View>
                            <View style={[s.bankRow, { marginBottom: 0 }]}>
                                <View style={s.bankCol}>
                                    <Text style={s.bankItemLabel}>Sort Code</Text>
                                    <Text style={s.bankItemMono}>{b.bank_details.sort_code}</Text>
                                </View>
                                <View style={s.bankCol}>
                                    <Text style={s.bankItemLabel}>Account Number</Text>
                                    <Text style={s.bankItemMono}>{b.bank_details.account_number}</Text>
                                </View>
                            </View>
                        </View>
                    </View>
                )}

                {/* ── Notes ── */}
                {invoice.notes && (
                    <View style={s.notesSection} wrap={false}>
                        <Text style={s.notesTitle}>Notes:</Text>
                        <Text style={s.notesText}>{invoice.notes}</Text>
                    </View>
                )}

                {/* ── Custom footer text ── */}
                {b.invoice_footer_text && (
                    <View style={s.footerText}>
                        <Text style={{ textAlign: 'center' }}>{b.invoice_footer_text}</Text>
                    </View>
                )}

                {/* No hardcoded sign-off.
                    There was a fixed "Thank you for your business!" here, on top
                    of `invoice_footer_text` rendered just above — which is the
                    field Settings → Shop Identity writes and whose placeholder
                    is literally "Thank you for your business!". Anyone who
                    filled it in got the line printed twice. The setting is the
                    single source: set nothing and nothing prints. */}

            </Page>
        </Document>
    );
};